import { useState } from "react";
import { Plus, Minus } from "@phosphor-icons/react";
import { Section, PageHero, EyebrowLabel, CalloutCard } from "@/components/marketing/MarketingUI";
import useSEO from "@/lib/useSEO";

const FAQS = [
  {
    group: "Product",
    items: [
      {
        q: "What kind of drillhole data does SRES accept?",
        a: "SRES accepts standard Collar, Survey, Assay and Geology CSV / XLSX files, plus QAQC control samples (CRM, blank, duplicate). Column names are auto-mapped from industry aliases (BHID → hole_id, easting → x, from/to intervals, etc.) so most files import without manual re-mapping.",
      },
      {
        q: "Which estimation methods are supported?",
        a: "Inverse-Distance Weighting (IDW), Nearest Neighbour and Ordinary Kriging with fitted spherical, exponential and Gaussian variogram models. Kriging supports composite lengths and per-lithology domaining.",
      },
      {
        q: "Do you support Measured / Indicated / Inferred classification?",
        a: "Yes — classification is opt-in per project. When enabled, blocks are classified based on search neighbourhood coverage and the number of informing samples.",
      },
      {
        q: "Which commodities does SRES support?",
        a: "Fe, Au, Cu, Ag, Ni, Zn, Pb, U₃O₈, lithium, REEs and industrial minerals. Each commodity ships with a canonical grade unit, price basis and default cut-off ranges.",
      },
      {
        q: "Can I export block models to Datamine, Surpac or Vulcan?",
        a: "Yes — block models can be exported as CSV, GeoJSON, Shapefile (.shp) and DWG-ready formats. Format compatibility layers are added on request via the enterprise onboarding programme.",
      },
    ],
  },
  {
    group: "Reporting & compliance",
    items: [
      {
        q: "Which reporting standards does SRES align to?",
        a: "JORC (Australasia), NI 43-101 (Canada) and SAMREC (South Africa). CPR templates map onto the guidance documents cited by each code, including Table 1 support for JORC.",
      },
      {
        q: "Can I add my Competent Person signature to the report?",
        a: "Yes. The Report Setup dialog accepts Prepared / Reviewed / Approved signature images that flow into the assembled CPR PDF and DOCX.",
      },
      {
        q: "Do public share links have watermarks?",
        a: "Yes — every public CPR share link is watermarked with the project name, share token and issue date. Share links carry an expiry, view counter and audit log.",
      },
    ],
  },
  {
    group: "Platform & security",
    items: [
      {
        q: "Is SRES multi-tenant?",
        a: "Yes. Each company is scoped to its own tenant with company-scoped admin, user validity windows and role-based permissions.",
      },
      {
        q: "Where is my data stored?",
        a: "Data is stored in region-scoped MongoDB clusters. Enterprise tenants can select India, Europe or Australia residency during onboarding.",
      },
      {
        q: "Do you offer SSO / SAML?",
        a: "Yes — SAML and OpenID Connect SSO are available on the enterprise plan. Reach out via the contact form to scope your identity-provider integration.",
      },
      {
        q: "How do you handle audit trails?",
        a: "Every user action, permission change, export and share-link view is captured in an admin-visible audit log with configurable retention.",
      },
    ],
  },
  {
    group: "Commercial",
    items: [
      {
        q: "How is SRES priced?",
        a: "SRES is priced by named users and by project throughput. Enterprise packages bundle premium onboarding, priority support and data-residency choices. Contact us to scope a quote for your team.",
      },
      {
        q: "Do you offer a trial?",
        a: "Yes. Every serious enquiry starts with a 45-minute working session on your own drillhole data. If we build a defensible model, you get a two-week trial workspace.",
      },
      {
        q: "Can I pilot SRES on a single project first?",
        a: "Absolutely. Most engagements begin with a single project pilot before we roll out to the wider resource practice.",
      },
    ],
  },
];

export default function FAQPage() {
  useSEO({
    title: "FAQ · Answers to the questions we hear the most",
    description: "Frequently asked questions about SRES — product, reporting, platform and commercial. Grouped for scanning.",
    keywords: "SRES FAQ, frequently asked questions, kriging FAQ, JORC FAQ",
    path: "/faq",
  });
  return (
    <div data-testid="faq-page">
      <PageHero
        eyebrow="Frequently asked questions"
        title="Answers to the questions we hear the most."
        description="Grouped into product, reporting, platform and commercial themes. If your question is not covered here, drop us a line — we always update this page."
        testid="faq-hero"
      />

      <Section className="py-20 md:py-24" testid="faq-body">
        <div className="space-y-14">
          {FAQS.map((group, gi) => (
            <div key={group.group} data-testid={`faq-group-${gi}`}>
              <EyebrowLabel className="mb-5">{group.group}</EyebrowLabel>
              <div className="space-y-3">
                {group.items.map((f, i) => (
                  <FAQItem key={f.q} q={f.q} a={f.a} testid={`faq-item-${gi}-${i}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-16 md:py-24 bg-[#F7F8FB]" testid="faq-cta">
        <CalloutCard
          eyebrow="Still have questions?"
          title="Ask us anything. A geologist will get back to you within one business day."
          description="No pre-canned support scripts. Real people, real answers."
          cta="Contact us"
          to="/contact"
          testid="faq-cta-callout"
        />
      </Section>
    </div>
  );
}

function FAQItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`rounded-xl border transition-colors ${
        open ? "border-brand-blue/50 bg-brand-blue/5" : "border-slate-200 bg-white"
      }`}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 p-5 text-left"
        data-testid={`${testid}-toggle`}
      >
        <span className="font-heading font-bold text-slate-900 text-[15px] leading-snug pr-4">{q}</span>
        <span className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 transition-colors ${
          open ? "bg-brand-blue text-white" : "bg-slate-100 text-slate-500"
        }`}>
          {open ? <Minus size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
        </span>
      </button>
      {open ? (
        <div className="px-5 pb-5 -mt-1 text-slate-700 text-[14px] leading-relaxed" data-testid={`${testid}-answer`}>
          {a}
        </div>
      ) : null}
    </div>
  );
}
